import React from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { NativeSelect } from '../ui/NativeSelect';
import { GoalFilters, GoalSort, GoalStatus } from '@/types/goals';
import { Search, Filter, X, SortAsc, SortDesc } from 'lucide-react';

interface GoalFiltersProps {
  filters: GoalFilters;
  sort: GoalSort;
  onFiltersChange: (filters: GoalFilters) => void;
  onSortChange: (sort: GoalSort) => void;
  onClearFilters: () => void;
  totalCount: number;
  filteredCount: number;
  aspirations?: Array<{ id: string; title: string }>;
}

// Categories removed - goals are organized by aspirations

const statuses: GoalStatus[] = ['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled'];

const sortOptions = [
  { field: 'title', label: 'Title' },
  { field: 'priority', label: 'Priority' },
  { field: 'progress', label: 'Progress' },
  { field: 'deadline', label: 'Deadline' },
  { field: 'status', label: 'Status' },
  { field: 'updatedAt', label: 'Updated' },
] as const;

export function GoalFiltersComponent({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalCount,
  filteredCount,
  aspirations = []
}: GoalFiltersProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = React.useState(false);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  // Category filtering removed - goals are organized by aspirations

  const toggleStatus = (status: GoalStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];

    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined
    });
  };

  const toggleAspiration = (aspirationId: string) => {
    const currentAspirations = filters.aspirationId || [];
    const newAspirations = currentAspirations.includes(aspirationId)
      ? currentAspirations.filter(a => a !== aspirationId)
      : [...currentAspirations, aspirationId];

    onFiltersChange({
      ...filters,
      aspirationId: newAspirations.length > 0 ? newAspirations : undefined
    });
  };

  const toggleOverdueFilter = () => {
    onFiltersChange({
      ...filters,
      isOverdue: filters.isOverdue ? undefined : true
    });
  };

  const toggleDeadlineFilter = () => {
    onFiltersChange({
      ...filters,
      hasDeadline: filters.hasDeadline ? undefined : true
    });
  };

  const handleSortChange = (field: GoalSort['field']) => {
    if (sort.field === field) {
      onSortChange({
        field,
        direction: sort.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      onSortChange({ field, direction: 'asc' });
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.status?.length ||
    filters.aspirationId?.length ||
    filters.isOverdue ||
    filters.hasDeadline
  );

  const getActiveFiltersCount = () => {
    return [
      filters.search ? 1 : 0,
      filters.status?.length || 0,
      filters.aspirationId?.length || 0,
      filters.isOverdue ? 1 : 0,
      filters.hasDeadline ? 1 : 0
    ].reduce((a, b) => a + b, 0);
  };

  return (
    <Card>
      <CardContent className="filters-content">
        {/* Mobile: Compact toggle button */}
        <div className="filters-mobile">
          <div className="layout-row-between">
            <span className="text-muted-sm">
              Showing {filteredCount} of {totalCount} goals
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters & Sort
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Desktop: Always visible controls */}
        <div className="filters-desktop">
          {/* Search and Sort Row */}
          <div className="filters-row">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search goals..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="layout-row-gap">
              <NativeSelect
                value={sort.field}
                onChange={(e) => handleSortChange(e.target.value as GoalSort['field'])}
              >
                {sortOptions.map(option => (
                  <option key={option.field} value={option.field}>
                    Sort by {option.label}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
              >
                {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
              </Button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>
          </div>

          {/* Results Summary */}
          <div className="filters-summary">
            <span>
              Showing {filteredCount} of {totalCount} goals
            </span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-xs"
              >
                <X className="icon-sm mr-1" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Desktop Expanded Filters */}
          {isExpanded && (
            <div className="filters-expanded">
              {/* Categories removed - goals are organized by aspirations */}

              {/* Statuses */}
              <div className="filters-section">
                <label className="text-label">Status</label>
                <div className="filters-buttons">
                  {statuses.map(status => (
                    <button
                      key={status}
                      onClick={() => toggleStatus(status)}
                      className={`filters-btn ${
                        filters.status?.includes(status) ? 'filters-btn-active' : ''
                      }`}
                    >
                      {formatStatus(status)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Aspirations */}
              {aspirations.length > 0 && (
                <div className="filters-section">
                  <label className="text-label">Linked to Aspiration</label>
                  <div className="filters-buttons">
                    {aspirations.map(aspiration => (
                      <button
                        key={aspiration.id}
                        onClick={() => toggleAspiration(aspiration.id)}
                        className={`filters-btn ${
                          filters.aspirationId?.includes(aspiration.id) ? 'filters-btn-active' : ''
                        }`}
                      >
                        {aspiration.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Filters */}
              <div className="filters-section">
                <label className="text-label">Quick Filters</label>
                <div className="filters-buttons">
                  <button
                    onClick={toggleOverdueFilter}
                    className={`filters-btn ${
                      filters.isOverdue ? 'filters-btn-active filters-btn-danger' : ''
                    }`}
                  >
                    Overdue
                  </button>
                  <button
                    onClick={toggleDeadlineFilter}
                    className={`filters-btn ${
                      filters.hasDeadline ? 'filters-btn-active' : ''
                    }`}
                  >
                    Has Deadline
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Expanded filters */}
        {isMobileFiltersOpen && (
          <div className="filters-mobile filters-expanded">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search goals..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="filters-section">
              <label className="text-label">Sort</label>
              <div className="layout-row-gap">
                <NativeSelect
                  value={sort.field}
                  onChange={(e) => handleSortChange(e.target.value as GoalSort['field'])}
                  className="flex-1"
                >
                  {sortOptions.map(option => (
                    <option key={option.field} value={option.field}>
                      Sort by {option.label}
                    </option>
                  ))}
                </NativeSelect>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
                >
                  {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
                </Button>
              </div>
            </div>

            {/* Categories removed - goals are organized by aspirations */}

            {/* Statuses */}
            <div className="filters-section">
              <label className="text-label">Status</label>
              <div className="filters-buttons">
                {statuses.map(status => (
                  <button
                    key={status}
                    onClick={() => toggleStatus(status)}
                    className={`filters-btn ${
                      filters.status?.includes(status) ? 'filters-btn-active' : ''
                    }`}
                  >
                    {formatStatus(status)}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Filters */}
            <div className="filters-section">
              <label className="text-label">Quick Filters</label>
              <div className="filters-buttons">
                <button
                  onClick={toggleOverdueFilter}
                  className={`filters-btn ${
                    filters.isOverdue ? 'filters-btn-active filters-btn-danger' : ''
                  }`}
                >
                  Overdue
                </button>
                <button
                  onClick={toggleDeadlineFilter}
                  className={`filters-btn ${
                    filters.hasDeadline ? 'filters-btn-active' : ''
                  }`}
                >
                  Has Deadline
                </button>
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <div className="pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="text-xs"
                >
                  <X className="icon-sm mr-1" />
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
